import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

const STORAGE_KEY_LEFT = 'adminColumnWidthLeft';
const STORAGE_KEY_RIGHT = 'adminColumnWidthRight';
const STORAGE_KEY_LEGACY = 'adminColumnWidth';
export const COLUMN_WIDTH_MIN = 10;
export const COLUMN_WIDTH_MAX = 250;
export const COLUMN_WIDTH_DEFAULT = 102;

function clampColumnWidth(value: number): number {
  const n = Number(value);
  if (Number.isNaN(n)) return COLUMN_WIDTH_DEFAULT;
  const clamped = Math.round(Math.max(COLUMN_WIDTH_MIN, Math.min(COLUMN_WIDTH_MAX, n)));
  return clamped;
}

function loadFromStorage(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null || raw === '') return COLUMN_WIDTH_DEFAULT;
    const parsed = parseInt(raw, 10);
    return clampColumnWidth(parsed);
  } catch {
    return COLUMN_WIDTH_DEFAULT;
  }
}

function loadColumnWidthLeft(): number {
  const fromLeft = loadFromStorage(STORAGE_KEY_LEFT);
  try {
    const legacy = localStorage.getItem(STORAGE_KEY_LEGACY);
    if (legacy != null && legacy !== '') {
      const parsed = parseInt(legacy, 10);
      const valid = clampColumnWidth(parsed);
      if (fromLeft === COLUMN_WIDTH_DEFAULT) {
        localStorage.setItem(STORAGE_KEY_LEFT, String(valid));
        return valid;
      }
    }
  } catch {
    // ignore
  }
  return fromLeft;
}

function loadColumnWidthRight(): number {
  const fromRight = loadFromStorage(STORAGE_KEY_RIGHT);
  try {
    const legacy = localStorage.getItem(STORAGE_KEY_LEGACY);
    if (legacy != null && legacy !== '') {
      const parsed = parseInt(legacy, 10);
      const valid = clampColumnWidth(parsed);
      if (fromRight === COLUMN_WIDTH_DEFAULT) {
        localStorage.setItem(STORAGE_KEY_RIGHT, String(valid));
        try {
          localStorage.removeItem(STORAGE_KEY_LEGACY);
        } catch {
          // ignore
        }
        return valid;
      }
    }
  } catch {
    // ignore
  }
  return fromRight;
}

interface AdminAppearanceContextValue {
  columnWidthLeft: number;
  columnWidthRight: number;
  setColumnWidthLeft: (value: number) => void;
  setColumnWidthRight: (value: number) => void;
  setBothColumnWidths: (left: number, right: number) => void;
}

const AdminAppearanceContext = createContext<AdminAppearanceContextValue | null>(null);

export const AdminAppearanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [columnWidthLeft, setColumnWidthLeftState] = useState<number>(COLUMN_WIDTH_DEFAULT);
  const [columnWidthRight, setColumnWidthRightState] = useState<number>(COLUMN_WIDTH_DEFAULT);
  const [isLoaded, setIsLoaded] = useState(false);
  const latestRef = useRef({ left: columnWidthLeft, right: columnWidthRight });
  latestRef.current = { left: columnWidthLeft, right: columnWidthRight };

  // Pobierz globalne ustawienia wyglądu z serwera przy starcie (dla wszystkich użytkowników)
  useEffect(() => {
    console.log('[AdminAppearance] Pobieranie globalnych ustawień z API /appearance...');
    api.get<{ column_width_left: number; column_width_right: number }>('/appearance')
      .then((res) => {
        console.log('[AdminAppearance] Odpowiedź z API:', res.data);
        const left = res.data?.column_width_left;
        const right = res.data?.column_width_right;
        if (typeof left === 'number' && typeof right === 'number') {
          const validLeft = clampColumnWidth(left);
          const validRight = clampColumnWidth(right);
          console.log('[AdminAppearance] Ustawiam szerokości:', { left: validLeft, right: validRight });
          setColumnWidthLeftState(validLeft);
          setColumnWidthRightState(validRight);
          try {
            localStorage.setItem(STORAGE_KEY_LEFT, String(validLeft));
            localStorage.setItem(STORAGE_KEY_RIGHT, String(validRight));
          } catch {
            // ignore
          }
        }
        setIsLoaded(true);
      })
      .catch((err) => {
        console.log('[AdminAppearance] Błąd API:', err);
        // Błąd API - użyj wartości z localStorage lub domyślnych
        const left = loadColumnWidthLeft();
        const right = loadColumnWidthRight();
        console.log('[AdminAppearance] Używam wartości z localStorage:', { left, right });
        setColumnWidthLeftState(left);
        setColumnWidthRightState(right);
        setIsLoaded(true);
      });
  }, []);

  const saveToServer = useCallback((left: number, right: number) => {
    console.log('[AdminAppearance] Zapisuję na serwer:', { left, right });
    api.put('/admin/appearance', {
      column_width_left: left,
      column_width_right: right,
    })
    .then((res) => {
      console.log('[AdminAppearance] Zapisano na serwerze:', res.data);
    })
    .catch((err) => {
      console.log('[AdminAppearance] Błąd zapisu na serwer:', err);
    });
  }, []);

  const setColumnWidthLeft = useCallback((value: number) => {
    const valid = clampColumnWidth(value);
    console.log('[AdminAppearance] setColumnWidthLeft:', value, '->', valid);
    setColumnWidthLeftState(valid);
    try {
      localStorage.setItem(STORAGE_KEY_LEFT, String(valid));
    } catch {
      // ignore storage errors
    }
    saveToServer(valid, latestRef.current.right);
  }, [saveToServer]);

  const setColumnWidthRight = useCallback((value: number) => {
    const valid = clampColumnWidth(value);
    console.log('[AdminAppearance] setColumnWidthRight:', value, '->', valid);
    setColumnWidthRightState(valid);
    try {
      localStorage.setItem(STORAGE_KEY_RIGHT, String(valid));
    } catch {
      // ignore storage errors
    }
    saveToServer(latestRef.current.left, valid);
  }, [saveToServer]);

  const setBothColumnWidths = useCallback((left: number, right: number) => {
    const validLeft = clampColumnWidth(left);
    const validRight = clampColumnWidth(right);
    console.log('[AdminAppearance] setBothColumnWidths:', { left, right }, '->', { validLeft, validRight });
    setColumnWidthLeftState(validLeft);
    setColumnWidthRightState(validRight);
    try {
      localStorage.setItem(STORAGE_KEY_LEFT, String(validLeft));
      localStorage.setItem(STORAGE_KEY_RIGHT, String(validRight));
    } catch {
      // ignore storage errors
    }
    // NIE wywołujemy saveToServer - AdminAppearance już to zrobił
  }, []);

  const value: AdminAppearanceContextValue = {
    columnWidthLeft,
    columnWidthRight,
    setColumnWidthLeft,
    setColumnWidthRight,
    setBothColumnWidths,
  };

  return (
    <AdminAppearanceContext.Provider value={value}>
      {children}
    </AdminAppearanceContext.Provider>
  );
};

export function useAdminAppearance(): AdminAppearanceContextValue {
  const ctx = useContext(AdminAppearanceContext);
  if (ctx == null) {
    return {
      columnWidthLeft: COLUMN_WIDTH_DEFAULT,
      columnWidthRight: COLUMN_WIDTH_DEFAULT,
      setColumnWidthLeft: () => {},
      setColumnWidthRight: () => {},
      setBothColumnWidths: () => {},
    };
  }
  return ctx;
}
